import React from "react";

import { useQuery } from "@apollo/client";
import {
  AccountBox as AccountBoxIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  CardMembership as CardMembershipIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  Grid,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import NavLink from "@/components/NavLink";
import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";

import EditIconButton from "../../components/IconButtons/EditIconButton";

const useStyles = makeStyles()((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    overflowY: "auto",
    padding: theme.spacing(4),
  },
  profilePanelWrapper: {
    display: "flex",
  },
  profilePanelBody: {
    backgroundColor: colors.white,
    width: "100%",
  },
  profileBanner: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    backgroundColor: colors.primary,
    height: "128px",
    paddingTop: "12px",
    paddingRight: "12px",
    position: "relative",
  },
  activeLink: {
    color: theme.palette.primary.main,
    "& .MuiListItemIcon-root": {
      color: "inherit",
    },
    "& .MuiListItemText-root": {
      color: "inherit",
    },
  },
  arrow: {
    height: "12px",
    width: "32px",
    marginLeft: "auto",
  },
  icon: {
    height: "28px",
    width: "28px",
    marginRight: "12px",
  },
  profilePicture: {
    position: "absolute",
    height: "112px",
    width: "112px",
    borderStyle: "solid",
    borderWidth: "4px",
    borderColor: colors.white,
    borderRadius: "56px",
    marginBottom: "18px",
    top: "64%",
    left: "34%",
    // On mobile the sidebar spans the full width, so anchor the avatar to the
    // horizontal center instead of the fixed desktop offset.
    [theme.breakpoints.down("md")]: {
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
  profileDetails: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    marginTop: "82px",
  },
  coachName: {
    color: colors.dark,
    fontSize: "18px",
    fontWeight: 400,
  },
  editButton: {
    backgroundColor: colors.white,
    marginLeft: "auto",
    padding: 0,
    "&:hover": {
      backgroundColor: colors.white,
    },
  },
  editButtonWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    width: "100%",
  },
  listHeader: {
    display: "flex",
    marginTop: "12px",
    padding: "18px",
  },
  listWrapper: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  list: {
    paddingLeft: "8px",
  },
  listItemText: {
    fontSize: "2px",
    color: colors.gray,
  },
  listItemIcon: {
    marginRight: 0,
    paddingRight: 0,
  },
  loadingView: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const profilePanelQuery = graphql(`
  query MyProfile {
    coachViewer {
      id
      account {
        id
        email
      }
      profile {
        fullName
        avatarUrl
      }
    }
  }
`);

const ProfilePanel: React.FC = ({ children }) => {
  const { classes: styles } = useStyles();
  const { data, loading } = useQuery(profilePanelQuery);
  const coachProfile = data?.coachViewer.profile;
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className={styles.loadingView}>
        <Typography variant="h6" color="textPrimary">
          Loading...
        </Typography>
      </div>
    );
  }
  return (
    <div className={styles.root}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={3} className={styles.profilePanelWrapper}>
          <Box className={styles.profilePanelBody}>
            <Box className={styles.profileBanner}>
              <div className={styles.editButtonWrapper}>
                <EditIconButton
                  onClick={() => navigate("edit-personal-info")}
                  className={styles.editButton}
                />
              </div>
              <Avatar
                className={styles.profilePicture}
                src={coachProfile?.avatarUrl ?? ""}
              />
            </Box>
            <div className={styles.profileDetails}>
              <Typography variant="subtitle1" className={styles.coachName}>
                {coachProfile?.fullName}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {data?.coachViewer.account.email}
              </Typography>
            </div>
            <div className={styles.listWrapper}>
              <Box className={styles.listHeader}>
                <Typography variant="subtitle1">Other Settings</Typography>
              </Box>
              <Divider />
              <List component="nav" className={styles.list}>
                <ListItem
                  component={NavLink}
                  to="personal-info"
                  activeClassName={styles.activeLink}
                >
                  <ListItemIcon className={styles.listItemIcon}>
                    <AccountBoxIcon className={styles.icon} />
                  </ListItemIcon>
                  <ListItemText className={styles.listItemText}>
                    Personal Information
                  </ListItemText>
                  <ListItemIcon>
                    <ArrowForwardIosIcon className={styles.arrow} />
                  </ListItemIcon>
                </ListItem>

                <Divider />
                <ListItem
                  component={NavLink}
                  to="trainings-and-specialization"
                  activeClassName={styles.activeLink}
                >
                  <ListItemIcon className={styles.listItemIcon}>
                    <CardMembershipIcon className={styles.icon} />
                  </ListItemIcon>
                  <ListItemText className={styles.listItemText}>
                    Trainings and Specialization
                  </ListItemText>
                  <ListItemIcon>
                    <ArrowForwardIosIcon className={styles.arrow} />
                  </ListItemIcon>
                </ListItem>

                <Divider />
                <ListItem
                  component={NavLink}
                  to="account"
                  activeClassName={styles.activeLink}
                >
                  <ListItemIcon className={styles.listItemIcon}>
                    <PersonIcon className={styles.icon} />
                  </ListItemIcon>
                  <ListItemText className={styles.listItemText}>
                    Account
                  </ListItemText>
                  <ListItemIcon>
                    <ArrowForwardIosIcon className={styles.arrow} />
                  </ListItemIcon>
                </ListItem>

                <Divider />
                <ListItem
                  component={NavLink}
                  to={"change-password"}
                  state={{ email: data?.coachViewer.account.email }}
                  activeClassName={styles.activeLink}
                >
                  <ListItemIcon className={styles.listItemIcon}>
                    <LockIcon className={styles.icon} />
                  </ListItemIcon>
                  <ListItemText className={styles.listItemText}>
                    Change Password
                  </ListItemText>
                  <ListItemIcon>
                    <ArrowForwardIosIcon className={styles.arrow} />
                  </ListItemIcon>
                </ListItem>
              </List>
            </div>
          </Box>
        </Grid>
        <Grid item xs={12} md={9}>
          {children}
        </Grid>
      </Grid>
    </div>
  );
};

export default ProfilePanel;
